"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "How do I register as a verified supplier?",
  answer: "Registration is streamlined through our secure portal. Once you submit your business credentials and trade history, our compliance team reviews your profile within 48 hours to ensure you meet our quality standards for the KTEX marketplace."
}, {
  question: "What are the benefits of becoming a premium supplier?",
  answer: "Premium suppliers gain priority access to high-volume WTB requests, enhanced visibility in our product directories, and dedicated account management to help optimize your trade listings for maximum reach."
}, {
  question: "How are payments handled on the platform?",
  answer: "We utilize a secure escrow-based payment system. Funds are verified upon order confirmation and released to your account once the buyer confirms receipt of goods, ensuring a safe and transparent transaction for both parties."
}, {
  question: "Can I manage my inventory in real-time?",
  answer: "Yes. Our supplier dashboard provides a robust interface for real-time inventory management, allowing you to update stock levels, adjust pricing, and manage bulk order inquiries instantly."
}, {
  question: "What support is available for new suppliers?",
  answer: "Every new supplier is assigned a trade specialist. From your initial onboarding to your first successful transaction, our team is available to assist with platform navigation, listing optimization, and logistics coordination."
}, {
  question: "How does KTEX ensure trade security?",
  answer: "Security is our foundation. We employ multi-layered verification for all participants, encrypted communication channels, and rigorous dispute resolution protocols to maintain a professional and trustworthy trading environment."
}, {
  question: "Are there fees for listing products?",
  answer: "Basic listing is free for all verified suppliers. We operate on a transparent commission model based on successful transactions, ensuring that our success is directly aligned with yours."
}, {
  question: "How quickly can I start receiving orders?",
  answer: "Once your profile is verified and your first products are listed, you become immediately visible to our global network of buyers. Many suppliers receive their first inquiries within the first week of activation."
}];
export default function SupplierFaq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2463" id="supplierfaq" className="bg-white text-[#1a1a1a] py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-sans text-xs tracking-widest uppercase text-[#1a3a52] mb-8">
                SUPPLIER SUPPORT / 03
              </div>

              <h2 className="font-default font-bold text-[#1a1a1a] mb-10" style={{
              fontSize: "clamp(2.75rem, 5vw, 5rem)",
              lineHeight: "0.95",
              letterSpacing: "-0.03em"
            }}>
                Supplier
                <br />
                Frequently
                <br />
                Asked.
              </h2>

              <p className="font-sans text-base md:text-lg leading-relaxed mb-10" style={{
              maxWidth: "360px",
              color: "#4a4a4a"
            }}>
                We are here to support your growth. Find answers to common questions about joining our network, managing your trade, and scaling your business.
              </p>

              <Button variant="outline" size="lg" className="font-default font-semibold tracking-wider uppercase text-xs border-[#1a3a52] text-white bg-[#1a3a52] hover:bg-[#1a3a52]/90 transition-all duration-300 px-8 py-6 h-auto rounded-lg">
                Contact Support
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 transition-all duration-300 group rounded-lg" style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e0e0e0",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
            }}>
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden group-hover:[border-color:#1a3a52]">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-[#1a3a52] shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-[#1a1a1a] leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-[#1a3a52] shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-sans text-base md:text-[1.0625rem]" style={{
                    lineHeight: "1.75",
                    maxWidth: "600px",
                    color: "#4a4a4a"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}